// Ported from `app/read/[id]/page.tsx` and its sibling `loading.tsx` in
// `git@git.fugl.dev:sks/yominoki.git` @ `515ce239`.
//
// The two files become one component. Next gave a route a `loading.tsx` for
// free by suspending the server component; a client route has to render its
// own pending state, so `loading.tsx`'s skeleton is the `loading` branch here —
// same markup, same proportions, so the shell does not jump when the body
// lands.
//
// `notFound()` covered two states the endpoint still keeps separate from each
// other and merged from here: no session, and no such reading. `GET
// /api/reading/:id` answers 404 to both on purpose (a reading id is a
// capability, and a 401 would tell an anonymous prober that an id exists), so
// this page has one not-found branch, exactly as the server component did.

import { useParams } from "react-router";
import { Skeleton } from "@bindery/ui/components/ui/skeleton";
import { BlueskyChrome } from "../components/reader/bluesky-chrome";
import { DistinboxChrome } from "../components/reader/distinbox-chrome";
import { useResource } from "../hooks/use-resource";
import { getReading } from "../lib/api";

export function ReadPage() {
  const { id } = useParams();
  const readingId = id ?? "";
  const { resource } = useResource(
    (signal) => getReading(readingId, signal),
    [readingId],
  );

  if (resource.status === "loading") return <ReadingSkeleton />;
  if (resource.status === "error") {
    return (
      <main className="mx-auto max-w-[36rem] px-6 py-16">
        <p className="n5-marginalia">this reading is not available</p>
      </main>
    );
  }

  const data = resource.data;
  return data.reading.source === "bluesky" ? (
    <BlueskyChrome data={data} />
  ) : (
    <DistinboxChrome data={data} />
  );
}

function ReadingSkeleton() {
  return (
    <main className="mx-auto max-w-[36rem] px-6 py-16">
      <header className="flex items-center gap-3">
        <Skeleton className="size-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>
      </header>
      <hr className="border-rule my-8 border-t" />
      <div className="space-y-4">
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-[95%]" />
        <Skeleton className="h-5 w-[90%]" />
        <Skeleton className="h-5 w-[88%]" />
        <Skeleton className="h-5 w-[84%]" />
      </div>
    </main>
  );
}
